import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  getAppDateRangePickerMode,
  isShareOfVoicePath,
  shouldDisableFutureDatesInTopPicker,
  useAppDateRange,
} from '@/contexts/AppDateRangeContext';
import { AccountSwitcher } from '@/components/AccountSwitcher';
import { DashboardDateRangePicker } from '@/components/dashboard/DashboardDateRangePicker';
// import { ConsolidatedViewButton } from '@/components/snapshot/ConsolidatedViewButton';
import { WorkspaceDateRangeControls } from '@/components/workspace/WorkspaceDateRangeControls';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { Spinner } from '@/components/ui/Spinner';
import { flipkartAdsAccountsQueryKey } from '@/components/FlipkartAdsOauthConnections';
import { adsApi, flipkartAdsApi } from '@/api/client';
import { getLocalTodayDate } from '@/utils/dateRangePicker';
import { isRangeValid, resolveSovDatePreset, SOV_DATE_PRESETS } from '@/api/reportDateRanges';
// import { isAmazonDashboardPath } from '@/api/consolidatedMetrics';
import { parseWorkspaceScope } from '@/api/workspaceScope';
import { describeFlipkartSyncStatus, formatSyncTime } from '@/api/syncStatus';
import { cn } from '@/utils/cn';

function SyncStatusLabel({ profileId }: { profileId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['profile-sync-status', profileId],
    queryFn: async () => {
      const res = await adsApi.getProfileSyncStatus(profileId);
      if (!res.success) throw new Error('Failed to load sync status');
      return res.data;
    },
    enabled: Boolean(profileId),
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 60_000 : 300_000,
  });

  if (isLoading) {
    return (
      <span className="hidden h-[34px] items-center gap-2 rounded-lg border border-border bg-surface px-3 text-body-sm text-foreground-muted shadow-sm md:inline-flex">
        <Spinner className="h-3.5 w-3.5" />
        Loading…
      </span>
    );
  }

  if (!data) return null;

  if (data.lastSyncedAt) {
    const label = `Last synced ${formatSyncTime(data.lastSyncedAt)}`;
    if (data.status === 'failed') {
      return (
        <span
          className="hidden h-[34px] max-w-[min(280px,35vw)] items-center truncate rounded-lg border border-border bg-surface px-3 text-body-sm text-warning-ink shadow-sm md:inline-flex"
          title={data.lastError ?? label}
        >
          {label} · sync failed
        </span>
      );
    }
    return (
      <span
        className="hidden h-[34px] max-w-[min(280px,35vw)] items-center truncate rounded-lg border border-border bg-surface px-3 text-body-sm text-foreground shadow-sm md:inline-flex"
        title={label}
      >
        {label}
      </span>
    );
  }

  return (
    <span className="hidden h-[34px] items-center rounded-lg border border-border bg-surface px-3 text-body-sm text-foreground-muted shadow-sm md:inline-flex">
      Not synced yet
    </span>
  );
}

function FlipkartSyncStatusLabel({
  credentialId,
  adAccountId,
}: {
  credentialId: string;
  adAccountId: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: flipkartAdsAccountsQueryKey(credentialId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listAccounts(credentialId);
      if (!res.success) throw new Error('Failed to load Flipkart ad accounts');
      return res.data.accounts;
    },
    enabled: Boolean(credentialId),
    refetchInterval: 300_000,
  });

  if (isLoading) {
    return (
      <span className="hidden h-[34px] items-center gap-2 rounded-lg border border-border bg-surface px-3 text-body-sm text-foreground-muted shadow-sm md:inline-flex">
        <Spinner className="h-3.5 w-3.5" />
        Loading…
      </span>
    );
  }

  if (!data) return null;

  const account = data.find((item) => item.ad_account_id === adAccountId);
  const label = describeFlipkartSyncStatus(account?.synced_at);
  return (
    <span
      className="hidden h-[34px] max-w-[min(280px,35vw)] items-center truncate rounded-lg border border-border bg-surface px-3 text-body-sm text-foreground shadow-sm md:inline-flex"
      title={label}
    >
      {label}
    </span>
  );
}

export function TopBar({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const location = useLocation();
  const scope = parseWorkspaceScope(location.pathname);
  const amazonProfileId = scope?.platform === 'amazon' ? scope.accountId : null;
  const flipkartScope = scope?.platform === 'flipkart' ? scope : null;
  const pickerMode = getAppDateRangePickerMode(location.pathname);
  const { range, setRange, setCurrentRange } = useAppDateRange();
  const maxSelectableDate = shouldDisableFutureDatesInTopPicker(location.pathname)
    ? getLocalTodayDate()
    : undefined;
  const sovDatePicker = isShareOfVoicePath(location.pathname);
  // Hidden for now — open via ?view=consolidated. Uncomment import + button to restore TopBar entry.
  // const showConsolidatedView = scope?.platform === 'amazon' && isAmazonDashboardPath(location.pathname);
  const activeSovPresetId = sovDatePicker
    ? SOV_DATE_PRESETS.find(
        (preset) =>
          range.current.startDate === resolveSovDatePreset(preset.id).startDate &&
          range.current.endDate === resolveSovDatePreset(preset.id).endDate
      )?.id
    : undefined;

  return (
    <header
      className={cn(
        'flex shrink-0 items-center gap-3 overflow-hidden px-6',
        compact ? 'h-[52px] min-h-[52px] py-1.5' : 'h-auto min-h-[60px] py-2'
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        <AccountSwitcher />
        {pickerMode === 'compare' ? (
          <div className="hidden min-w-0 flex-1 overflow-x-auto md:block">
            <WorkspaceDateRangeControls
              value={range}
              onChange={setRange}
              compact
              toDate={maxSelectableDate}
            />
          </div>
        ) : null}
        {pickerMode === 'simple' ? (
          <div className="hidden items-center gap-2 md:flex">
            <span className="shrink-0 whitespace-nowrap text-body-sm text-foreground-muted">
              Date range
            </span>
            <DashboardDateRangePicker
              startDate={range.current.startDate}
              endDate={range.current.endDate}
              onChange={setCurrentRange}
              invalidRange={!isRangeValid(range.current)}
              variant="default"
              showHelp={false}
              toDate={maxSelectableDate}
              presets={sovDatePicker ? SOV_DATE_PRESETS : undefined}
              activePresetId={activeSovPresetId}
              onPresetSelect={
                sovDatePicker ? (id) => setCurrentRange(resolveSovDatePreset(id)) : undefined
              }
            />
          </div>
        ) : null}
        {pickerMode === 'current-only' ? (
          <div className="hidden min-w-0 flex-1 overflow-x-auto md:block">
            <WorkspaceDateRangeControls
              value={range}
              onChange={setRange}
              compact
              showCompare={false}
            />
          </div>
        ) : null}
      </div>
      {/* Consolidated View TopBar entry — uncomment imports + showConsolidatedView above to restore.
      {showConsolidatedView ? (
        <span className="inline-flex shrink-0">
          <ConsolidatedViewButton />
        </span>
      ) : null}
      */}
      <div className="flex shrink-0 items-center gap-3">
        {amazonProfileId ? <SyncStatusLabel profileId={amazonProfileId} /> : null}
        {flipkartScope ? (
          <FlipkartSyncStatusLabel
            credentialId={flipkartScope.credentialId}
            adAccountId={flipkartScope.accountId}
          />
        ) : null}
        <span
          className="hidden max-w-[min(180px,20vw)] truncate text-body-md text-foreground-muted sm:inline"
          title={user?.email ?? undefined}
        >
          {user?.email}
        </span>
        <SignOutButton />
      </div>
    </header>
  );
}
